import React, { useEffect, useState, useRef } from "react";
import "../../index.css";
import "../../App.css";
import {
  Row,
  Col,
  Input,
  Typography,
  Radio,
  Tag,
  Button,
  Checkbox,
  Spin,
} from "antd";
import NewEntryFormLeftColWrapper from "../wrappers/NewEntryFormLeftColWrapper";
import NewEntryFormRightColWrapper from "../wrappers/NewEntryFormRightColWrapper";
import RatePainOrderCreamSelector from "./RatePainOrderCream";
import { RootState, useAppDispatch } from "../../redux-toolkit/store/store";
import { useSelector } from "react-redux";
import {
  handleGetStartedFormFields,
  handleGoToNextStep,
  handlePainDescriptionChange,
  handleUpdateErrorsOfOrderCreamForm,
  handleUpdateOrderCreamPainLocations,
  handleUpdatePainProfileDuration,
  handleGoBack,
  saveStep1Api,
  getOrderByTransactionIdApi,
} from "../../redux-toolkit/slices/orderCreamSlice";
import { handleUpdatePainLocations } from "../../redux-toolkit/slices/painEntryFormSlice";
import { useSearchParams } from "react-router-dom";
import { validatePainProfile } from "../../schemas/OrderCream";
import ErrorList from "../Items/ErrorsList";
import MaleBodyViewer from "../../MaleBodyViewer";
// import OrderCreamMaleBodyViewer from "../../OrderCreamMaleBodyViewer";

const painDescriptionOptions: string[] = [
  "Throbbing",
  "Pounding",
  "Shooting",
  "Stabbing/sharp",
  "Pinching",
  "Hot/Cold",
  "Tingling/numbness",
  "Stinging",
  "Dull",
  "Sore",
  "Heavy",
  "Tender",
  "Burning",
  "Stiffness",
  "Cramping",
  "Swollen",
];

const OcAboutPain = () => {
  const orderCreamForm = useSelector((state: RootState) => state.orderCream);
  const { isLoading } = orderCreamForm;
  const painEntryForm = useSelector((state: RootState) => state.painEntryForm);
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const isSyncingRef = useRef(false);

  // Fetch order data when component mounts
  useEffect(() => {
    const fetchOrder = async () => {
      if (orderCreamForm.transactionId && !hasInitialized) {
        setIsLoadingOrder(true);
        try {
          const orderData = await dispatch(
            getOrderByTransactionIdApi(orderCreamForm.transactionId)
          ).unwrap();
          // The Redux reducer will automatically populate the state with order data
          // Sync painEntryForm.painLocations from orderCreamForm.painProfile.painLocations
          if (
            orderData?.painProfile?.painLocations &&
            orderData.painProfile.painLocations.length > 0
          ) {
            dispatch(
              handleUpdatePainLocations(orderData.painProfile.painLocations)
            );
          }
          setHasInitialized(true);
        } catch (error) {
          console.error("Error loading order:", error);
        } finally {
          setIsLoadingOrder(false);
        }
      }
    };

    fetchOrder();
  }, [orderCreamForm.transactionId, dispatch, hasInitialized]);

  // Sync painEntryForm.painLocations to orderCreamForm when user selects body parts
  // Use a ref to track the last synced value to prevent infinite loops
  const lastSyncedRef = useRef<string>("");

  useEffect(() => {
    if (!hasInitialized) return; // Don't sync until initial load is complete

    const painEntryLocations = painEntryForm.painLocations || [];
    const currentKey = JSON.stringify([...painEntryLocations].sort());

    // Only sync if the value has actually changed from what we last synced
    if (currentKey !== lastSyncedRef.current) {
      lastSyncedRef.current = currentKey;
      dispatch(
        handleUpdateOrderCreamPainLocations(painEntryForm.painLocations)
      );
    }
  }, [painEntryForm.painLocations, dispatch, hasInitialized]);

  const {
    painProfile,
    isError,
    errors,
    demoGraphics,
    transactionId,
    currentStep,
  } = orderCreamForm;

  const handlePaindurationChange = (e: any) => {
    dispatch(handleUpdatePainProfileDuration(e.target.value));
  };

  const handleBack = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", prevStep.toString());
      setSearchParams(newParams, { replace: true });
      // Don't call handleGoBack() - let the parent component sync from URL
    }
  };
  const handleSubmit = async () => {
    // Validate radio button first - must be either "acute" or "chronic"
    const painDurationValue = painProfile.painDuration;
    const validValues = ["acute", "chronic"];
    
    if (!painDurationValue || 
        painDurationValue === "" || 
        painDurationValue === null || 
        painDurationValue === undefined ||
        painDurationValue === "pending" ||
        (typeof painDurationValue === 'string' && painDurationValue.trim() === "") ||
        !validValues.includes(painDurationValue)) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Please choose a duration."],
        })
      );
    }

    // Remove _id before validation
    const { _id, ...painProfileWithoutId } = painProfile as any;
    const { isError, errors } = validatePainProfile(painProfileWithoutId);

    if (isError) {
      return dispatch(handleUpdateErrorsOfOrderCreamForm({ isError, errors }));
    }

    if (!transactionId) {
      return dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Transaction ID is missing. Please refresh the page."],
        })
      );
    }

    try {
      // Sanitize painProfile to remove _id fields
      const sanitizedPainProfile = {
        painRating: painProfile.painRating,
        painDuration: painProfile.painDuration,
        painDescriptions: painProfile.painDescriptions || [],
        painLocations: painProfile.painLocations || [],
      };

      await dispatch(
        saveStep1Api({
          transactionId,
          painProfile: sanitizedPainProfile,
        })
      ).unwrap();

      // Update URL with new step
      const newParams = new URLSearchParams(searchParams);
      newParams.set("step", "2");
      setSearchParams(newParams, { replace: true });

      dispatch(handleGoToNextStep());
    } catch (error) {
      dispatch(
        handleUpdateErrorsOfOrderCreamForm({
          isError: true,
          errors: ["Failed to save step. Please try again."],
        })
      );
    }
  };
  return (
    <div className="aboutpain-entry">
      <div className="flex flex-col gap-5 w-full">
        {/* Pain Location Section */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Select the area(s) where your pain is. This is where you
              experience the most discomfort. *
            </strong>
          </label>
          <MaleBodyViewer isBodyClickEnable={true} isOrderCreamEntry={true} />
        </div>

        {/* Pain Rating Section */}
        <div className="flex flex-col gap-2">
          <RatePainOrderCreamSelector />
        </div>

        {/* Pain Duration Section */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>How long have you had this pain? *</strong>
          </label>
          <Radio.Group
            onChange={handlePaindurationChange}
            value={painProfile.painDuration}
          >
            <Radio value="acute">Acute - less than 3 months</Radio>
            <Radio value="chronic">Chronic - more than 3 months</Radio>
          </Radio.Group>
        </div>

        {/* Pain Description Section */}
        <div className="flex flex-col gap-2">
          <label className="h6 text-theme-text-secondary">
            <strong>
              Pick 3 words that best describe your pain snd how it feels. *
            </strong>
            <span className="text-theme-text-primary ml-2">
              ({painProfile.painDescriptions.length} of 3 selected)
            </span>
          </label>
          <p className="body1 text-theme-text-secondary mb-4">
            (You can always add more detais later)
          </p>
          <div className="tag-container-compact">
            {painDescriptionOptions.map((option) => (
              <Tag.CheckableTag
                key={option}
                checked={painProfile.painDescriptions.includes(option)}
                onChange={() => dispatch(handlePainDescriptionChange(option))}
                className="checkable-tag-pain-entry"
              >
                {option}
              </Tag.CheckableTag>
            ))}
          </div>
        </div>
        {/* Error Display */}
        {isError === true && (
          <div className="flex justify-center" style={{ marginTop: "40px" }}>
            <ErrorList errors={errors} />
          </div>
        )}

        {/* Submit Button */}
        <div
          className="flex justify-center gap-4"
          style={{ marginTop: "40px" }}
        >
          <button
            onClick={handleBack}
            className="caption2 transition-colors duration-200"
            style={{
              color: "var(--primary-500)",
              textDecoration: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Previous
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg"
            disabled={isLoading}
            style={{ position: "relative", minWidth: "120px" }}
          >
            {isLoading ? (
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Spin size="small" />
                Saving...
              </span>
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OcAboutPain;
